import React, { ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import { transition } from '../../styles/animations';

export interface Props {
    /** Diameter of the SVG circle in pixels. */
    size?: number;
    /** Width of the circular stroke in pixels; clamped to half the size minus 1. */
    strokeWidth?: number;
    /** Color of the unfilled (background) track circle. */
    background?: string;
    /** Color of the filled progress arc. */
    strokeColor?: string;
    /** Progress value from 0 to 1 representing the fraction of the circle to fill. */
    completeCondition: number;
    /** CSS class applied to the root element. */
    className?: string;
    children?: ReactNode;
}

const HBProgressCircle = ({
    size = 96,
    strokeWidth = 8,
    background = vars.colors.surface.neutral,
    strokeColor = vars.colors.base.accent,
    completeCondition,
    className,
    children
}: Props) => {
    const strokeWidthCapped = Math.min(strokeWidth, size / 2 - 1);
    const radius = Math.max((size - strokeWidthCapped) / 2 - 1, 0);
    const circumference = 2 * Math.PI * radius;
    const completeConditionFunction = () => completeCondition;
    const dashOffset = (1 - completeConditionFunction()) * circumference;
    const [offset, setOffset] = React.useState(-90);

    useEffect(() => {
        setOffset(/firefox/i.test(navigator.userAgent) ? -89.9 : -90);
    }, []);

    return (
        <HBProgressCircleContainer className={className}>
            <svg width={size} height={size} view-box={`0 0 ${size} ${size}`}>
                <circle
                    fill="none"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={background}
                    strokeWidth={strokeWidthCapped}
                />
                <circle
                    fill="none"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={strokeColor}
                    strokeWidth={strokeWidthCapped}
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    transform={`rotate(${offset}, ${size / 2}, ${size / 2})`}
                    strokeLinecap="round"
                />
            </svg>
            {children && (
                <ChildrenContainer size={size}>{children}</ChildrenContainer>
            )}
        </HBProgressCircleContainer>
    );
};

export default HBProgressCircle;

// -------------------------------- Styles -------------------------------- //

const ChildrenContainer = styled.div<{ size: number }>`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    justify-content: center;
    align-items: center;
`;

const HBProgressCircleContainer = styled.div`
    position: relative;
    circle {
        ${transition()}
    }
`;
